import React from 'react';
import { motion } from 'framer-motion';
import { FlaskConical, Truck, ShieldCheck, Star } from 'lucide-react';
import { CTA } from './CTA';
import { Vial } from './Vial';
import { UKFlag } from './UKFlag';
const PILLARS = [{
  icon: FlaskConical,
  title: 'Third-party tested',
  body: 'HPLC verified every batch'
}, {
  icon: Truck,
  title: 'Same-day UK dispatch',
  body: 'Tracked cold-chain delivery'
}, {
  icon: ShieldCheck,
  title: '30-day guarantee',
  body: 'Re-shipped if compromised'
}];
export function Hero() {
  return <section className="relative w-full overflow-hidden" data-id="element-287">
      <div className="container mx-auto pt-14 lg:pt-20 pb-20 lg:pb-28" data-id="element-288">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-12 items-center" data-id="element-289">
          {/* Copy */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} className="lg:col-span-7" data-id="element-290">
            {/* Eyebrow */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-card mb-7" data-id="element-291">
              <UKFlag size={14} data-id="element-292" />
              <span className="text-[11px] font-medium tracking-wide uppercase text-muted-foreground" data-id="element-293">
                Shipped from the UK
                <span className="text-border mx-1.5" data-id="element-294">·</span>
                HPLC tested
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-semibold tracking-[-0.035em] text-foreground text-[42px] leading-[1.02] sm:text-[56px] lg:text-[74px]" data-id="element-295">
              Lab-grade <span className="text-primary" data-id="element-296">peptides</span>
              <span className="text-primary" data-id="element-297">,</span>
              <br data-id="element-298" />
              <span className="text-foreground" data-id="element-299">purity in every </span>
              <span className="text-primary" data-id="element-300">.ml</span>
            </h1>

            {/* Subhead */}
            <p className="mt-6 max-w-[560px] text-[17px] leading-[1.55] text-muted-foreground" data-id="element-301">
              Every batch independently HPLC verified at{' '}
              <span className="font-mono text-foreground" data-id="element-302">≥99%</span> purity by
              an ISO 17025 accredited UK lab. Same-day dispatch, cold-chain,
              discreet packaging.
            </p>

            {/* CTAs */}
            <div className="mt-9 flex flex-wrap items-center gap-3" data-id="element-303">
              <CTA to="/shop" variant="primary" size="lg" data-id="element-304">
                Shop peptides
              </CTA>
              <CTA href="#lab" variant="secondary" size="lg" data-id="element-305">
                View lab results
              </CTA>
            </div>

            {/* Trustpilot indicator */}
            <div className="mt-8 flex items-center gap-3" data-id="element-306">
              <div className="flex items-center gap-0.5" aria-label="4.8 out of 5" data-id="element-307">
                {Array.from({
                length: 5
              }).map((_, i) => <Star key={i} className="h-4 w-4 fill-primary text-primary" strokeWidth={0} data-id="element-308" />)}
              </div>
              <span className="text-[13px] text-muted-foreground" data-id="element-309">
                <span className="font-mono text-foreground" data-id="element-310">4.8</span>
                <span className="text-border mx-1.5" data-id="element-311">·</span>
                <span className="font-mono text-foreground" data-id="element-312">1,247</span> reviews
                on{' '}
                <span className="font-semibold text-foreground" data-id="element-313">
                  Trustpilot
                </span>
              </span>
            </div>
          </motion.div>

          {/* Visual */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.15,
          ease: [0.22, 1, 0.36, 1]
        }} className="lg:col-span-5" data-id="element-314">
            <div className="relative w-full max-w-[460px] mx-auto" data-id="element-315">
              {/* Soft tonal backdrop */}
              <div className="absolute inset-x-6 top-6 bottom-10 rounded-[28px] bg-secondary/60" data-id="element-316" />
              <div className="absolute inset-x-12 top-14 bottom-20 rounded-[22px] bg-card border border-border" data-id="element-317" />

              {/* Vial */}
              <div className="relative flex items-end justify-center pt-12 pb-12" data-id="element-318">
                <motion.div animate={{
                y: [0, -6, 0]
              }} transition={{
                duration: 5,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="text-foreground" data-id="element-319">
                  <Vial label="BPC-157" size={340} data-id="element-320" />
                </motion.div>

                {/* Annotation: purity (top-right) */}
                <div className="hidden sm:flex absolute top-8 right-0 lg:right-2 items-center gap-3" data-id="element-321">
                  <span className="hidden md:block h-px w-10 bg-foreground/20" data-id="element-322" />
                  <div className="rounded-lg bg-background border border-border px-3 py-2 shadow-sm" data-id="element-323">
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-324">
                      Purity
                    </div>
                    <div className="font-mono text-base text-foreground font-medium leading-none mt-0.5" data-id="element-325">
                      99.7%
                    </div>
                  </div>
                </div>

                {/* Annotation: batch (bottom-left) */}
                <div className="hidden sm:flex absolute bottom-14 left-0 lg:left-2 items-center gap-3" data-id="element-326">
                  <div className="rounded-lg bg-background border border-border px-3 py-2 shadow-sm" data-id="element-327">
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-328">
                      Batch
                    </div>
                    <div className="font-mono text-[13px] text-foreground font-medium leading-none mt-0.5" data-id="element-329">
                      BP-24091
                    </div>
                  </div>
                  <span className="hidden md:block h-px w-10 bg-foreground/20" data-id="element-330" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}